import { Injectable } from '@angular/core';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ComplementaryReportFilesActions } from '../actions/complementary-report-files.actions';
import { ComplementaryReportFilesService } from '../services/complementary-report-files.service';

@Injectable()
export class ComplementaryReportFilesEffects {
    private openedFileUrls = new Map<string, { url: string; blob: Blob }>();

    constructor(
        private actions$: Actions,
        private service: ComplementaryReportFilesService,
        private coreProvider: AumicoCoreProvider,
    ) {}

    uploadComplementaryReportFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ComplementaryReportFilesActions.uploadComplementaryReportFile),
            switchMap(({ sortOrderId, printLocation, documentFile }) =>
                this.service.uploadComplementaryReportFile$(documentFile).pipe(
                    map((document) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_document_uploaded_success_title',
                            'aumico_cpmm_document_uploaded_success_description',
                            NotificationType.SUCCESS,
                        );
                        return ComplementaryReportFilesActions.uploadComplementaryReportFileSuccess({ sortOrderId, printLocation, document });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_document_uploaded_failure_title',
                            'aumico_cpmm_document_uploaded_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(ComplementaryReportFilesActions.uploadComplementaryReportFileFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    // Check preview status
        checkPreviewStatus$ = createEffect(() =>
            this.actions$.pipe(
                ofType(ComplementaryReportFilesActions.checkPreviewStatus),
                mergeMap(({ fileId }) =>
                    this.service.getPreviewStatus(fileId).pipe(
                        map(({ status }) => ComplementaryReportFilesActions.checkPreviewStatusSuccess({ fileId, status })),
                        catchError((error) => of(ComplementaryReportFilesActions.checkPreviewStatusFailure({ fileId, error }))),
                    ),
                ),
            ),
        );
        
        // Request preview generation
        requestPreviewGeneration$ = createEffect(() =>
            this.actions$.pipe(
                ofType(ComplementaryReportFilesActions.requestPreviewGeneration),
                mergeMap(({ fileId }) =>
                    this.service.requestPreviewGeneration(fileId).pipe(
                        map(({ requestId }) => ComplementaryReportFilesActions.requestPreviewGenerationSuccess({ fileId, requestId })),
                        catchError((error) => of(ComplementaryReportFilesActions.requestPreviewGenerationFailure({ fileId, error }))),
                    ),
                ),
            ),
        );
        
    // Load generated preview URL
    loadPreviewUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ComplementaryReportFilesActions.loadPreviewUrl),
            mergeMap(({ fileId }) =>
                this.service.getPreview(fileId).pipe(
                    map(({ url }) => ComplementaryReportFilesActions.loadPreviewUrlSuccess({ fileId, url })),
                    catchError((error) => of(ComplementaryReportFilesActions.loadPreviewUrlFailure({ fileId, error }))),
                ),
            ),
        ),
    );

    // Open file
    openFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ComplementaryReportFilesActions.openFile),
            switchMap(({ fileId }) => {
                return this.service.getFileContent(fileId).pipe(
                    map((blob) => {
                        const url = URL.createObjectURL(blob);
                        this.openedFileUrls.set(fileId, { url, blob });
                        return ComplementaryReportFilesActions.openFileSuccess({ fileId, url });
                    }),
                    catchError((error) => {
                        return of(ComplementaryReportFilesActions.openFileFailure({ fileId, error }));
                    }),
                );
            }),
        ),
    );

    // Revoke file URL (cleanup)
    revokeFileUrl$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ComplementaryReportFilesActions.revokeFileUrl),
                tap(({ fileId }) => {
                    const fileData = this.openedFileUrls.get(fileId);
                    if (fileData) {
                        URL.revokeObjectURL(fileData.url);
                        this.openedFileUrls.delete(fileId);
                    }
                }),
            ),
        { dispatch: false },
    );
}